/**
 * The admin area's calls (Phase 26). Every database function behind these
 * refuses anyone who isn't a platform admin (0036); nothing here is a guard.
 */

import { supabase } from '@/lib/supabase';
import { resetRedirectUrl } from '@/lib/auth-redirect';
import { functionErrorMessage } from '@/lib/data/team';

export interface AdminFirm {
  id: string;
  name: string;
  slug: string | null;
  status: 'active' | 'suspended';
  seat_limit: number;
  people: number;
  pending: number;
  owners: string | null;
  created_at: string;
}

export interface SupportSession {
  id: string;
  firm_id: string;
  firm_name: string;
  reason: string;
  expires_at: string;
}

export interface SupportLogEntry {
  id: string;
  admin_email: string;
  firm_name: string;
  reason: string;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
}

function fail(error: { message?: string } | null): never {
  throw new Error(error?.message || 'That didn’t work. Please try again.');
}

export async function listFirms(): Promise<AdminFirm[]> {
  const { data, error } = await supabase.rpc('admin_list_firms');
  if (error) fail(error);
  return (data as AdminFirm[] | null) ?? [];
}

/** The firm row only; invite its owner next with inviteFirmOwner(). */
export async function createFirm(name: string, slug: string | null): Promise<string> {
  const { data, error } = await supabase.rpc('admin_create_firm', { p_name: name, p_slug: slug ?? '' });
  if (error) fail(error);
  return data as string;
}

/** Invite a firm's owner, through the same invite-member function owners use. */
export async function inviteFirmOwner(firmId: string, email: string, origin: string = location.origin): Promise<void> {
  const { error } = await supabase.functions.invoke('invite-member', {
    body: { email: email.trim(), firmId, role: 'owner', redirectTo: resetRedirectUrl(origin) },
  });
  if (error) throw new Error(await functionErrorMessage(error));
}

export async function setFirmStatus(firmId: string, status: 'active' | 'suspended'): Promise<void> {
  const { error } = await supabase.rpc('admin_set_firm_status', { p_firm: firmId, p_status: status });
  if (error) fail(error);
}

export async function addPlatformAdmin(email: string): Promise<void> {
  const { error } = await supabase.rpc('add_platform_admin', { p_email: email.trim() });
  if (error) fail(error);
}

export async function mySupportSession(): Promise<SupportSession | null> {
  const { data, error } = await supabase.rpc('my_support_session');
  if (error) fail(error);
  return ((data as SupportSession[] | null) ?? [])[0] ?? null;
}

export async function startSupportSession(firmId: string, reason: string, minutes: number): Promise<void> {
  const { error } = await supabase.rpc('start_support_session', {
    p_firm: firmId, p_reason: reason.trim(), p_minutes: minutes,
  });
  if (error) fail(error);
}

export async function endSupportSession(): Promise<void> {
  const { error } = await supabase.rpc('end_support_session');
  if (error) fail(error);
}

export async function listSupportSessions(limit = 50): Promise<SupportLogEntry[]> {
  const { data, error } = await supabase.rpc('admin_list_support_sessions', { p_limit: limit });
  if (error) fail(error);
  return (data as SupportLogEntry[] | null) ?? [];
}
